import { ColumnValue, ColumnValueSegment } from '@opengov/reporting-types';
import { RowsMetadata } from './rowDataTransforms';

export type PartitionedParentQueryRows = {
  parentRow: ColumnValueSegment[];
  rootBreakdownRows: ColumnValueSegment[][];
};

export function buildRootBreakdownCacheKey(rowGroupIndex: number, parentGroupId: string | undefined): string {
  return `${rowGroupIndex}:${parentGroupId ?? 'default'}`;
}

/**
 * A breakdown row injected at level 0 while its root is collapsed (BUD-16094).
 *
 * Bulk expansion must skip these. They are a stand-in shown only while the root is closed, and
 * opening the root deletes them — so a run that targets both makes the two undo each other, and the
 * root loses: it ends up closed with the stand-in expanded in its place. Expanding the root is the
 * correct outcome, because the child fetch then supplies the same breakdown beneath it.
 */
export function isInjectedRootBreakdownRow(node: { data?: unknown } | null | undefined): boolean {
  return Boolean((node?.data as { isRootLevelBreakdown?: boolean } | undefined)?.isRootLevelBreakdown);
}

function isBreakdownGroupId(rowsMetadata: RowsMetadata, groupId: ColumnValueSegment): boolean {
  if (groupId == null) {
    return false;
  }
  const groupKey = String(groupId);
  if (!(groupKey in rowsMetadata)) {
    return false;
  }
  return rowsMetadata[groupKey].isBreakdownRow === true;
}

/**
 * Splits a parent-query pivot response into the root aggregate row and first-level breakdown rows.
 */
export function partitionParentQueryRows(
  data: ColumnValueSegment[][] | undefined,
  rowsMetadata: RowsMetadata
): PartitionedParentQueryRows {
  if (!data || data.length === 0) {
    return { parentRow: [], rootBreakdownRows: [] };
  }

  const parentRow = data.find((row) => {
    const groupId = row[0];
    return groupId != null && !isBreakdownGroupId(rowsMetadata, groupId);
  });

  const resolvedParent = parentRow ?? data[0];

  const rootBreakdownRows = data.filter((row) => {
    const groupId = row[0];
    return isBreakdownGroupId(rowsMetadata, groupId);
  });

  return { parentRow: resolvedParent, rootBreakdownRows };
}

export type FilterBreakdownFromChildRowsResult = {
  filteredRows: ColumnValue[];
  totalRowIndex: number | undefined;
};

/**
 * Removes breakdown rows already shown from the parent query so child fetches do not duplicate them.
 */
export function filterBreakdownFromChildRows(
  childRows: ColumnValue[] | undefined,
  rowsMetadata: RowsMetadata,
  cachedBreakdownGroupIds: Set<string>,
  totalRowIndex: number | undefined
): FilterBreakdownFromChildRowsResult {
  if (!childRows?.length) {
    return { filteredRows: [], totalRowIndex };
  }

  const filteredRows: ColumnValue[] = [];
  let removedBeforeTotals = 0;

  for (let index = 0; index < childRows.length; index++) {
    const row = childRows[index];
    const groupId = String(row[0] ?? '');
    const isCachedBreakdown = cachedBreakdownGroupIds.has(groupId) && isBreakdownGroupId(rowsMetadata, groupId);

    if (isCachedBreakdown) {
      if (typeof totalRowIndex === 'number' && index < totalRowIndex) {
        removedBeforeTotals += 1;
      }
      continue;
    }
    filteredRows.push(row);
  }

  const adjustedTotalRowIndex = typeof totalRowIndex === 'number' ? totalRowIndex - removedBeforeTotals : totalRowIndex;

  return { filteredRows, totalRowIndex: adjustedTotalRowIndex };
}

export type RootBreakdownCacheEntry = {
  breakdownGroupIds: Set<string>;
  breakdownGridRows: object[];
  /** Measure values from the parent-query root row (used to align totals row with the root Totals column). */
  parentMeasureValues: ColumnValueSegment[];
  /** True when breakdown rows were added at level 0 via SSRM transaction (collapsed root). */
  visibleAtRootLevel: boolean;
};

export function parseRootBreakdownCacheKey(cacheKey: string): {
  rowGroupIndex: number;
  parentGroupId: string;
} {
  const colonIndex = cacheKey.indexOf(':');
  return {
    rowGroupIndex: Number(cacheKey.slice(0, colonIndex)),
    parentGroupId: cacheKey.slice(colonIndex + 1)
  };
}

type ParentRowForTotalsOverride = {
  isBreakdownRow?: boolean;
  isRootLevelBreakdown?: boolean;
  values?: ColumnValueSegment[];
};

/**
 * Measures for the child-fetch totals row. Root-level breakdown rows (collapsed-root case, level 0)
 * and deep symbiote breakdown rows (level 2+) return undefined so the API's own totals are preserved.
 * Level-1 symbiote breakdown rows (under an expanded root) override the totals row with the
 * breakdown's own values. Non-breakdown rows override with the parent aggregate.
 */
export function resolveParentMeasureValuesForChildTotals(
  parentRow: ParentRowForTotalsOverride | undefined,
  parentNodeLevel: number
): ColumnValueSegment[] | undefined {
  if (!parentRow) {
    return undefined;
  }

  if (parentRow.isRootLevelBreakdown || (parentRow.isBreakdownRow && parentNodeLevel > 1)) {
    return undefined;
  }

  return parentRow.values;
}

/**
 * Replaces measure values on the child-fetch totals row so it matches the expanded parent row.
 */
type GridRowWithMeasures = { isTotalRow?: boolean; values?: ColumnValueSegment[] };

export function applyParentQueryValuesToRootChildTotalsRows<T extends GridRowWithMeasures>(
  gridRows: T[],
  parentMeasureValues: ColumnValueSegment[] | undefined,
  showTotalsRows: boolean | undefined
): T[] {
  if (showTotalsRows === false || !parentMeasureValues?.length) {
    return gridRows;
  }

  return gridRows.map((row) => {
    if (!row.isTotalRow) {
      return row;
    }
    return { ...row, values: [...parentMeasureValues] };
  });
}

export type RootBreakdownCache = Map<string, RootBreakdownCacheEntry>;

export function createRootBreakdownCache(): RootBreakdownCache {
  return new Map();
}

/**
 * Writes breakdown cache data without resetting visibleAtRootLevel when the grid has
 * already injected rows (buildAndEmitRows runs multiple times per parent fetch).
 */
export function setRootBreakdownCacheEntry(
  cache: RootBreakdownCache,
  cacheKey: string,
  entry: {
    breakdownGroupIds: Set<string>;
    breakdownGridRows: object[];
    parentMeasureValues: ColumnValueSegment[];
  }
): void {
  const existing = cache.get(cacheKey);
  cache.set(cacheKey, {
    breakdownGroupIds: entry.breakdownGroupIds,
    breakdownGridRows: entry.breakdownGridRows,
    parentMeasureValues: entry.parentMeasureValues,
    visibleAtRootLevel: existing?.visibleAtRootLevel ?? false
  });
}

/**
 * Parent-query breakdown group ids are only stripped from child fetches while those rows
 * are still shown at level 0 (collapsed root). When expanded, child rows must retain
 * per-subgroup breakdown rows that reuse the same group ids.
 */
export function getParentQueryBreakdownIdsForChildDedupe(
  entry: RootBreakdownCacheEntry | undefined
): Set<string> | undefined {
  return entry?.visibleAtRootLevel === true ? entry.breakdownGroupIds : undefined;
}

type ChildGridRow = { isTotalRow?: boolean; values?: ColumnValueSegment[]; groupId?: unknown };

/**
 * After a root-level breakdown row's children load (first page), syncs the breakdown row's
 * displayed values from the child-fetch totals row so collapsing back shows accurate
 * per-breakdown totals instead of the parent-query root aggregate.
 * Also updates the cache so re-injected rows (on root collapse) carry the correct values.
 *
 * `fallbackTotalsRowValues` supplies the totals row measures when the report hides totals rows
 * (showTotalsRows === false): in that mode the totals child row is stripped from `childGridRows`
 * before it reaches here, so without this fallback the breakdown row would stay permanently blank.
 */
export function syncBreakdownRowValuesFromChildTotals(
  parentNode: { setData?: (data: object) => void; data: object | undefined },
  nodeKey: string | undefined,
  rowGroupIndex: number,
  breakdownGroupId: string | undefined,
  childGridRows: ChildGridRow[],
  cache: RootBreakdownCache,
  fallbackTotalsRowValues?: ColumnValueSegment[]
): void {
  const totalsRowValues = childGridRows.find((r) => r.isTotalRow)?.values ?? fallbackTotalsRowValues;
  if (!totalsRowValues?.length) return;

  if (!nodeKey) return;
  const colonIdx = nodeKey.indexOf(':');
  if (colonIdx < 0) return;

  const rootGroupId = nodeKey.slice(0, colonIdx);
  const cacheKey = buildRootBreakdownCacheKey(rowGroupIndex, rootGroupId);
  const entry = cache.get(cacheKey);
  const updatedValues = [...totalsRowValues];

  if (entry && breakdownGroupId) {
    const updatedRows = entry.breakdownGridRows.map((row) => {
      const r = row as ChildGridRow;
      if (String(r.groupId) === breakdownGroupId) {
        return { ...r, values: updatedValues };
      }
      return row;
    });
    cache.set(cacheKey, { ...entry, breakdownGridRows: updatedRows });
  }

  if (typeof parentNode.setData === 'function') {
    parentNode.setData({ ...(parentNode.data as object), values: updatedValues });
  }
}
